/**
 * Suppress the browser gestures that fight a fullscreen game.
 *
 * iOS Safari (and Brave, which uses WebKit there) ignores `user-scalable=no`
 * and does not fully honour `touch-action: none` for double-tap zoom on
 * interactive elements. Tapping an action button quickly — exactly what combat
 * asks for — therefore zooms the page instead of attacking.
 *
 * These guards are deliberately narrow: they only fire inside the game root, so
 * any future out-of-canvas UI can still scroll and zoom normally.
 */

/** Two taps closer together than this are a zoom gesture, not two inputs. */
const DOUBLE_TAP_MS = 350;

export interface GestureGuardOptions {
  /** Only events inside this element are suppressed. */
  readonly root: HTMLElement;
}

export function installGestureGuards({ root }: GestureGuardOptions): () => void {
  let lastTouchEnd = 0;

  const withinRoot = (target: EventTarget | null): boolean =>
    target instanceof Node && root.contains(target);

  // Double-tap zoom. Cancelling the second tap's default is what stops it;
  // the first tap is left alone so single taps behave normally.
  const onTouchEnd = (event: TouchEvent): void => {
    if (!withinRoot(event.target)) return;
    const now = performance.now();
    if (now - lastTouchEnd < DOUBLE_TAP_MS) event.preventDefault();
    lastTouchEnd = now;
  };

  // Pinch zoom. These are WebKit-only and absent elsewhere, which is harmless.
  const onGesture = (event: Event): void => {
    if (withinRoot(event.target)) event.preventDefault();
  };

  // Ctrl+wheel is desktop browser zoom; a game canvas should never trigger it.
  const onWheel = (event: WheelEvent): void => {
    if (event.ctrlKey && withinRoot(event.target)) event.preventDefault();
  };

  // A long press pops up the callout/selection menu mid-fight.
  const onContextMenu = (event: MouseEvent): void => {
    if (withinRoot(event.target)) event.preventDefault();
  };

  // passive:false is required — a passive listener cannot preventDefault.
  document.addEventListener('touchend', onTouchEnd, { passive: false });
  document.addEventListener('gesturestart', onGesture, { passive: false });
  document.addEventListener('gesturechange', onGesture, { passive: false });
  document.addEventListener('gestureend', onGesture, { passive: false });
  document.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('contextmenu', onContextMenu);

  return () => {
    document.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('gesturestart', onGesture);
    document.removeEventListener('gesturechange', onGesture);
    document.removeEventListener('gestureend', onGesture);
    document.removeEventListener('wheel', onWheel);
    document.removeEventListener('contextmenu', onContextMenu);
  };
}

/**
 * True when a double tap should be treated as a zoom gesture rather than two
 * separate inputs. Extracted so the timing rule is testable.
 */
export function isDoubleTap(previousMs: number, currentMs: number): boolean {
  return currentMs - previousMs < DOUBLE_TAP_MS;
}
